import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import { toast } from "react-toastify";
import API_URL from "../../config";

function RegisterAdmin() {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Details
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    otp: "",
    securityCode: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.msg);
        setStep(2);
      } else {
        toast.error(data.msg);
      }
    } catch (error) {
      toast.error("Failed to send OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, otp: form.otp }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.msg);
        setStep(3);
      } else {
        toast.error(data.msg);
      }
    } catch (error) {
      toast.error("Failed to verify OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/register-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Admin account created successfully!");
        navigate("/login");
      } else {
        toast.error(data.msg);
      }
    } catch (error) {
      toast.error("Registration failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="min-h-screen flex items-center justify-center bg-sky-50 p-6">
        <div className="w-full max-w-5xl flex flex-col md:flex-row bg-white rounded-xl shadow-md overflow-hidden">
          {/* Left panel */}
          <div className="w-full md:w-1/2 bg-blue-600 text-white p-10 flex flex-col justify-center">
            <h2 className="text-3xl font-bold mb-3">Admin Portal Access</h2>
            <p className="text-lg text-blue-100 mb-8">
              Secure registration for authorized personnel only
            </p>

            <div className="space-y-5">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">🔒</span>
                <span className="text-blue-50 font-medium">Enhanced security protocols</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-2xl">⚙️</span>
                <span className="text-blue-50 font-medium">Full system management</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-2xl">📊</span>
                <span className="text-blue-50 font-medium">Analytics and reporting</span>
              </div>
            </div>

            {/* Step progress indicator */}
            <div className="mt-12 pt-8 border-t border-blue-400">
              <div className="flex items-center gap-2 mb-4">
                <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-white' : 'bg-blue-300'}`} />
                <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-white' : 'bg-blue-300'}`} />
                <div className={`h-2 flex-1 rounded-full ${step >= 3 ? 'bg-white' : 'bg-blue-300'}`} />
              </div>
              <p className="text-sm font-semibold text-blue-50 uppercase tracking-widest">
                {step === 1 && "Step 1: Admin Identity"}
                {step === 2 && "Step 2: Security Verification"}
                {step === 3 && "Step 3: Access Configuration"}
              </p>
            </div>
          </div>

          {/* Right panel (Forms) */}
          <div className="w-full md:w-1/2 p-10">
            {step === 1 && (
              <form onSubmit={handleSendOTP} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-800">Admin Register</h2>
                  <span className="px-3 py-1 text-xs font-black bg-red-100 text-red-600 rounded-full uppercase">Restricted</span>
                </div>
                <p className="text-sm text-gray-600">Enter your official email to start verification</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Official Email</label>
                  <input
                    type="email"
                    placeholder="admin@travelaura.com"
                    className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-lg text-white font-bold bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all active:scale-95"
                >
                  {isSubmitting ? "Initiating Verification..." : "Verify Identity →"}
                </button>
                <div className="text-sm text-center text-gray-600 pt-4">
                  Already have an admin account? <Link to="/login" className="text-blue-600 font-bold hover:underline">Sign in</Link>
                </div>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-800">Verify Code</h2>
                <p className="text-sm text-gray-600">A 6-digit security code was sent to your email</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Security Code</label>
                  <input
                    type="text"
                    maxLength="6"
                    placeholder="000000"
                    className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-2xl font-black tracking-widest outline-none transition"
                    value={form.otp}
                    onChange={(e) => setForm({ ...form, otp: e.target.value })}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-lg text-white font-bold bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all active:scale-95"
                >
                  {isSubmitting ? "Verifying..." : "Validate Access →"}
                </button>
                <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-500 hover:underline w-full text-center font-medium">
                  Correction in email? Go back
                </button>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={handleRegister} className="space-y-5">
                <h2 className="text-2xl font-bold text-gray-800">Finalize Profile</h2>
                <p className="text-sm text-gray-600">Provide your credentials and authorization code</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    placeholder="Full legal name"
                    className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Admin Password</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Authorization Security Code</label>
                  <input
                    type="password"
                    placeholder="Enter system code"
                    className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
                    value={form.securityCode}
                    onChange={(e) => setForm({ ...form, securityCode: e.target.value })}
                    required
                  />
                  <p className="text-[10px] text-gray-400 mt-1 uppercase font-black">Requires System Admin Authorization</p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-lg text-white font-bold bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all active:scale-95"
                >
                  {isSubmitting ? "Finalizing..." : "Complete Registration →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Fotter />
    </div>
  );
}

export default RegisterAdmin;